import {
  AdyenCheckout,
  Card,
  Dropin,
  CoreConfiguration,
} from '@adyen/adyen-web';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';

import { company } from '~/company/Company';
import { catalogStore } from '~/stores/CatalogStore';
import { checkoutStore } from '~/stores/CheckoutStore/CheckoutStore';
import { mainStore } from '~/stores/MainStore';
import { orderStore } from '~/stores/OrderStore';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import eventEmitter, { EventsName } from './Events';

const wait = () =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve('');
    }, 1000);
  });

class AdyenService {
  private component: Dropin | null = null;
  private _step = 1;
  private _orderId: string | null = null;
  private mediaQuery!: MediaQueryList;
  private paymentTimeoutId: NodeJS.Timeout | null = null;
  private currentPath: string | null = null;

  constructor() {
    makeAutoObservable(this);
    if (import.meta.env.SSR) {
      return;
    }

    this.currentPath = window.location.pathname;

    this.mediaQuery = window.matchMedia('(max-width: 1024px)');
    this.mediaQuery.addEventListener('change', this.handleResetPayment);

    eventEmitter.on(EventsName.PAYMENT_METHOD_CHANGED, this.handleResetPayment);
    window.addEventListener('beforeunload', this.handleResetPayment);

    this.setupNavigationTracking();
  }

  private setupNavigationTracking() {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      if (import.meta.env.SSR) {
        return;
      }
      this.handlePathChange(window.location.pathname);
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      if (import.meta.env.SSR) {
        return;
      }
      this.handlePathChange(window.location.pathname);
    };
  }

  private handlePathChange(newPath: string) {
    if (this.currentPath && this.currentPath !== newPath) {
      this.handleResetPayment();
    }
    this.currentPath = newPath;
  }

  cleanup() {
    this.clearPaymentTimeout();
    this.mediaQuery.removeEventListener('change', this.handleResetPayment);
    window.removeEventListener('popstate', this.handleResetPayment);
    window.removeEventListener('beforeunload', this.handleResetPayment);
  }

  private clearPaymentTimeout() {
    if (this.paymentTimeoutId) {
      clearTimeout(this.paymentTimeoutId);
      this.paymentTimeoutId = null;
    }
  }

  private handleResetPayment = () => {
    this.clearPaymentTimeout();
    if (this._step !== 1) {
      this._orderId = null;
      this.component?.unmount();
      this._step = 1;
    }
  };

  get step() {
    return this._step;
  }

  get orderId() {
    return this._orderId;
  }

  getButtonTitle(): string {
    return this._step === 1 ? t('confirmOrder') : t('pay');
  }

  async initPayment(
    sessionData: CoreConfiguration['session'],
    timeout: number,
    orderId: string,
  ) {
    Sentry.withScope((scope) => {
      scope.setTag('payment_flow', 'adyen_init');
      scope.setContext('adyen_session', {
        orderId,
        timeout,
        sessionId: sessionData?.id,
        environment: import.meta.env.REACT_APP_ENV,
      });
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Adyen payment initialization started',
        level: 'info',
      });
    });

    this._orderId = orderId;
    const checkout = await AdyenCheckout({
      clientKey: company.envVariables.REACT_APP_ADYEN_CLIENT_KEY,
      environment:
        import.meta.env.REACT_APP_ENV === 'production' ? 'live' : 'test',
      showPayButton: false,
      countryCode: company.config.countryCode || 'US',
      session: sessionData,
      onPaymentCompleted: async (result, component) => {
        Sentry.withScope((scope) => {
          scope.setContext('payment_result', { result });
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Adyen payment completed successfully',
            level: 'info',
          });
        });
        this.clearPaymentTimeout();
        component?.unmount();
        window.scrollTo(0, 200);
        await checkoutStore.finalizePayment();
        const storedPromocode = getStoredPromocode();

        if (
          catalogStore.promocode.success &&
          catalogStore.promocode.value === storedPromocode?.code
        ) {
          clearStoredPromocode();
        }
        this._step = 1;
      },
      onPaymentFailed: async (result, component) => {
        Sentry.withScope((scope) => {
          scope.setExtras({
            result,
          });
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Adyen payment failed',
            level: 'error',
          });
        });
        this.clearPaymentTimeout();
        await wait();
        component?.unmount();
        this._step = 1;

        mainStore.pushAlert('error', t('errors:paymentFailed'));
        checkoutStore.setIsLoading(false);

        throw new Error('[Adyen]: payment failed');
      },
      onError: (error, component) => {
        Sentry.withScope((scope) => {
          scope.setExtras({
            error,
          });
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Adyen payment error',
            level: 'error',
          });
        });
        this.clearPaymentTimeout();
        component?.unmount();
        this._step = 1;
        mainStore.pushAlert('error', error.message);
        checkoutStore.setIsLoading(false);
        throw new Error('[Adyen]: error initializing payment');
      },
    });

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen checkout configuration created',
      level: 'info',
    });

    this.component = new Dropin(checkout, {
      paymentMethodComponents: [Card],
      filterStoredPaymentMethods: (storedPaymentMethods) => {
        return storedPaymentMethods.filter(
          (method) =>
            !method.brand?.includes('google') &&
            !method.brand?.includes('apple'),
        );
      },
      paymentMethodsConfiguration: {
        card: {
          enableStoreDetails: true,
        },
      },
    }).mount('#adyen-dropin-container');

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen dropin component mounted',
      level: 'info',
    });

    checkoutStore.setIsLoading(false);
    this._step = 2;

    this.checkPaymentIsNotExpired(timeout);
  }

  submit() {
    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen payment submission triggered',
      level: 'info',
      data: {
        orderId: this._orderId,
        isValid: this.component?.isValid,
      },
    });
    this.component?.submit();
    if (this.component?.isValid) {
      checkoutStore.setIsLoading(true);
    }
  }

  checkPaymentIsNotExpired(timeout: number) {
    this.paymentTimeoutId = setTimeout(() => {
      Sentry.withScope((scope) => {
        scope.setExtras({
          timeout: timeout / 1000 + 'sec',
        });
        mainStore.pushAlert('error', t('errors:paymentExpired'));
        Sentry.captureMessage(
          '[Checkout] Adyen confirmCardPayment timeout 7min',
          'warning',
        );
      });
      this.handleResetPayment();
    }, timeout);
  }

  cancelPayment() {
    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen payment cancellation requested',
      level: 'info',
      data: {
        orderId: this._orderId,
      },
    });
    if (this._orderId) {
      orderStore.orderCancel(this._orderId).catch((error) => {
        Sentry.withScope((scope) => {
          scope.setExtras({ error });
          Sentry.captureMessage('Failed to cancel Adyen payment', 'error');
        });
        console.error(error);
      });
    }
  }

  unmount() {
    this.clearPaymentTimeout();
    this.component?.unmount();
    this._step = 1;
    this._orderId = null;
  }
}

const adyenService = new AdyenService();

export default adyenService;
